import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import session from "../../utils/session";
import { apiHelper } from "../../utils/api";
import { clearAllLocalAppData } from "../../utils/storage";

// Auth state: user data is stored for UI, auth is via httpOnly cookie (not in Redux/localStorage)
// On page refresh, user data is restored from persisted Redux state as a UX hint.
// The actual auth validation happens server-side on each API call via the httpOnly cookie.
const initialState = {
  user: session.getUser() || null,
  isAuthenticated: !!session.getUser(),
  loading: false,
  error: null,
};

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch }) => {
    // Dispatch logout immediately so UI updates without delay
    dispatch(logout());
    // Server-side logout and cleanup in background (non-blocking)
    apiHelper.post("/auth/logout").catch(() => {});
    clearAllLocalAppData().catch(() => {});
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.error = null;

      // Persist user data to localStorage for page refresh UX hint
      try {
        session.setUser(action.payload.user);
        // Store token in memory for raw fetch() fallback (NOT in localStorage)
        if (action.payload.token) {
          session.setToken(action.payload.token);
        }
      } catch (e) {
        // ignore
      }
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;

      try {
        session.clear();
        sessionStorage.removeItem("trial_modal_shown");
      } catch (e) {}
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      try {
        session.setUser(state.user);
      } catch (e) {}
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;
